/**
 * SpayNeuterAnswerForm (#1116 AC2) — the desk records the spay/neuter answer.
 *
 * Ted (09-29): the "Spay/neuter?" notice keeps showing for imported Gingr
 * records "until someone actually fills it in". Most of those owners have no
 * app login yet, so the someone is at the counter. Same three values as the
 * owner's question (#566): Yes — spayed, Yes — neutered, No. "Male or
 * female?" follows a No only, because the 7-month / 11-month reminder needs
 * it and a Yes already implies it.
 *
 * PATCHes /api/admin/dogs/:dogId/spay-neuter and asks the page to refetch:
 * the "Spay/neuter?" pill in the header is derived server-side on every
 * read, so it disappears the moment the server has the answer, and only then.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { humanizeApiError, readSession, updateDogSpayNeuter } from '../../lib/api.js';

export const SPAY_NEUTER_ANSWER_COPY = {
  label: 'Spayed or neutered?',
  placeholder: 'Choose Yes or No',
  spayed: 'Yes — spayed (female)',
  neutered: 'Yes — neutered (male)',
  intact: 'No — not spayed or neutered',
  missing: 'Choose Yes — spayed, Yes — neutered, or No.',
  sexLabel: 'Male or female?',
  save: 'Save answer',
} as const;

type Answer = '' | 'intact' | 'spayed' | 'neutered';
type Sex = '' | 'male' | 'female';

interface SpayNeuterAnswerFormProps {
  apiUrl: string;
  dogId: string;
  /** What is on file now, so the select opens on it. */
  current: 'intact' | 'spayed' | 'neutered' | 'altered' | null;
  currentSex: 'male' | 'female' | null;
  onSaved: () => void;
  onCancel: () => void;
}

export function SpayNeuterAnswerForm({
  apiUrl,
  dogId,
  current,
  currentSex,
  onSaved,
  onCancel,
}: SpayNeuterAnswerFormProps): JSX.Element {
  const [answer, setAnswer] = useState<Answer>(
    current === 'intact' || current === 'spayed' || current === 'neutered' ? current : '',
  );
  const [sex, setSex] = useState<Sex>(currentSex ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (answer === '') {
      setError(SPAY_NEUTER_ANSWER_COPY.missing);
      selectRef.current?.focus();
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired — sign in again to save.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateDogSpayNeuter(apiUrl, token, dogId, {
        alteredStatus: answer,
        // A No sends what the desk was told, or nothing. A Yes implies it
        // server-side; sending nothing keeps that inference in one place.
        sex: answer === 'intact' ? (sex === '' ? null : sex) : undefined,
      });
      onSaved();
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to save';
      setError(humanizeApiError(raw));
      setSubmitting(false);
    }
  };

  return (
    <form
      className="ferro-dogprofile__note-form"
      onSubmit={onSubmit}
      data-testid="dogprofile-spay-neuter-form"
      // Our sentence, not the browser's tooltip (the AddDogForm rule).
      noValidate
    >
      <label className="ferro-dogprofile__field-label" htmlFor="dogprofile-spay-neuter-answer">
        {SPAY_NEUTER_ANSWER_COPY.label}
      </label>
      <select
        id="dogprofile-spay-neuter-answer"
        name="alteredStatus"
        ref={selectRef}
        className="ferro-dogprofile__select"
        value={answer}
        onChange={(e) => setAnswer(e.target.value as Answer)}
        disabled={submitting}
        required
        aria-required="true"
        data-testid="dogprofile-spay-neuter-answer"
      >
        <option value="">{SPAY_NEUTER_ANSWER_COPY.placeholder}</option>
        <option value="spayed">{SPAY_NEUTER_ANSWER_COPY.spayed}</option>
        <option value="neutered">{SPAY_NEUTER_ANSWER_COPY.neutered}</option>
        <option value="intact">{SPAY_NEUTER_ANSWER_COPY.intact}</option>
      </select>
      {answer === 'intact' ? (
        <>
          <label className="ferro-dogprofile__field-label" htmlFor="dogprofile-spay-neuter-sex">
            {SPAY_NEUTER_ANSWER_COPY.sexLabel}
          </label>
          <select
            id="dogprofile-spay-neuter-sex"
            name="sex"
            className="ferro-dogprofile__select"
            value={sex}
            onChange={(e) => setSex(e.target.value as Sex)}
            disabled={submitting}
            data-testid="dogprofile-spay-neuter-sex"
          >
            <option value="">Not sure</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
          <p className="ferro-dogprofile__field-hint">
            The reminder goes out at 7 months for a female, 11 for a male — it needs this.
          </p>
        </>
      ) : null}
      {error && (
        <p className="ferro-dogprofile__form-error" role="alert" data-testid="dogprofile-spay-neuter-error">
          {error}
        </p>
      )}
      <div className="ferro-dogprofile__note-actions">
        <button
          type="submit"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
          disabled={submitting}
          data-testid="dogprofile-spay-neuter-save"
        >
          {submitting ? 'Saving…' : SPAY_NEUTER_ANSWER_COPY.save}
        </button>
        <button
          type="button"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
